import { DateTime } from "luxon";
import store from "@/store";

/**
 * Normalize an ISO string to UTC (suppress milliseconds)
 * - Already UTC → return as-is
 * - Explicit offset → convert to UTC
 * - No offset → interpret as `location` timezone and convert to UTC
 * - Non-date strings → return unchanged
 */
export function normalizeISOForRequest(value) {
    if (!value) return value;

    // Already UTC
    if (/[zZ]$/.test(value)) return value;

    // Explicit offset → respect, convert to UTC
    if (/[+-]\d{2}:\d{2}$/.test(value)) {
        return DateTime.fromISO(value).toUTC().toISO({ suppressMilliseconds: true });
    }

    // No suffix → interpret as user location → convert to UTC
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(value)) {
        const { location } = store.state.settings;
        return DateTime.fromISO(value, { zone: location }).toUTC().toISO({ suppressMilliseconds: true });
    }

    // Non-date string → return as-is
    return value;
}

/**
 * Convert any outgoing payload to JSON, normalizing Dates and ISO strings
 */
export function toUtcJson(value) {
    return JSON.stringify(value, (_, v) => {
        if (v instanceof Date) {
            return v.toISOString(); // always UTC + Z
        }
        if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(v)) {
            console.log(v, "v in toUtcJson");
            return normalizeISOForRequest(v);
        }
        return v;
    });
}

/**
 * Parse an ISO string from API (response side)
 * - Returns string in UTC format
 * - Leaves non-date strings untouched
 */
export function parseApiDate(value) {
    if (typeof value !== "string") return null;

    // Already ISO with Z or offset → return as-is
    if (/[zZ]$|([+-]\d{2}:\d{2})$/.test(value)) {
        return value;
    }

    // No suffix → append Z (assume UTC)
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{3})?)?$/.test(value)) {
        return value + "Z";
    }

    return null; // invalid format
}

/**
 * Recursively normalize all ISO strings in objects/arrays
 * - Leaves non-date strings untouched
 * - Preserves structure
 */
export function normalizeDates(obj) {
    if (Array.isArray(obj)) return obj.map((v) => normalizeDates(v));

    if (obj && typeof obj === "object") {
        const out = {};
        for (const key in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, key)) {
                out[key] = normalizeDates(obj[key], location);
            }
        }
        return out;
    }

    // Only normalize valid ISO strings
    if (typeof obj === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(obj)) {
        return parseApiDate(obj);
    }

    return obj; // everything else stays the same
}
